'use client';

import { encryptAES128 } from '@/lib/crypto-utils';
import { useViewCount } from '@/services/query/view.query';
import { useMemberStore } from '@/stores/member.store';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { shouldCountExamListEntry } from '../utils/exam-view-count-path';

const VIEW_COUNT_DEBOUNCE_MS = 2000;
const lastCounted: { path: string; at: number } = { path: '', at: 0 };

function shouldSkipDuplicateCount(currentPath: string): boolean {
  const now = Date.now();
  if (currentPath !== lastCounted.path) return false;
  return now - lastCounted.at < VIEW_COUNT_DEBOUNCE_MS;
}

function markCounted(currentPath: string): void {
  lastCounted.path = currentPath;
  lastCounted.at = Date.now();
}

/**
 * pathname 변경 시 시험안내 메인 리스트 "진입" 규칙에 따라 뷰 카운트만 수행.
 * - 시험안내 리스트: 밖에서 리스트로 들어올 때만 1회
 * - 동일 path에 대해 짧은 시간 내 재호출은 스킵 (Strict Mode 이중 실행 방지)
 */
export function useExamViewCount() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const prevPathRef = useRef<string | null>(null);
  const storeHash = useMemberStore((s) => s.hash);
  const { mutate: viewCount } = useViewCount();

  const encryptLocationParam =
    searchParams.get('encryptLocation')?.trim() ?? '';
  const rawLocationParam = searchParams.get('location')?.trim() ?? '';
  const effectiveLocation =
    encryptLocationParam ||
    (rawLocationParam ? (encryptAES128(rawLocationParam) ?? '') : '');

  const effectiveHash = (
    searchParams.get('hash')?.trim() ??
    storeHash ??
    ''
  ).trim();

  useEffect(() => {
    const currentPath = pathname ?? '';
    const prevPath = prevPathRef.current;

    if (!effectiveHash) {
      prevPathRef.current = currentPath;
      return;
    }

    if (!shouldCountExamListEntry(prevPath ?? '', currentPath)) {
      prevPathRef.current = currentPath;
      return;
    }

    if (shouldSkipDuplicateCount(currentPath)) {
      prevPathRef.current = currentPath;
      return;
    }

    viewCount({
      type: '면허시험안내',
      hash: effectiveHash,
      location: effectiveLocation,
    });

    markCounted(currentPath);
    prevPathRef.current = currentPath;
  }, [pathname, effectiveHash, effectiveLocation, viewCount]);
}
